import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ENDPOINTS, fetchWithLog } from '../config';
import './Chatbot.css';

function getSessionId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 's-' + Date.now() + '-' + Math.random().toString(36).slice(2);
}

const PAGE_ROUTES = 'Available pages: Home (/), About (/about), Services (/services), Skills (/skills), Portfolio (/portfolio), Contact (/contact). When recommending a page, use markdown links: [Page Name](/path). Example: "Check the [About](/about) page" or "Use [Contact](/contact) to get in touch."';

function renderMessageWithLinks(text, onLinkClick) {
  if (!text || typeof text !== 'string') return text;
  const parts = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
    const url = match[2];
    const isInternal = url.startsWith('/') && !url.startsWith('//');
    parts.push({ type: 'link', text: match[1], url, internal: isInternal });
    lastIndex = regex.lastIndex;
  }
  parts.push({ type: 'text', content: text.slice(lastIndex) });
  return parts.map((p, i) => {
    if (p.type === 'text') return <span key={i}>{p.content}</span>;
    if (p.internal) return <Link key={i} to={p.url} className="chatbot-msg-link" onClick={onLinkClick}>{p.text}</Link>;
    return <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" className="chatbot-msg-link" onClick={onLinkClick}>{p.text}</a>;
  });
}

const stripHtml = (html) => (html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

/** Detect if the user is asking to speak to a human / rep / agent */
function wantsToSpeakToHuman(text) {
  if (!text || typeof text !== 'string') return false;
  const t = text.toLowerCase().trim();
  const patterns = [
    /\b(speak|talk)\s+to\s+(a\s+)?(rep|human|agent|person|someone|somebody)\b/,
    /\b(real\s+)?(human|person|agent)\b/,
    /\blive\s+agent\b/,
    /\bconnect\s+(me\s+)?(with|to)\s+(a\s+)?(rep|human|agent|person)\b/,
    /\b(need|want|like)\s+to\s+(speak|talk)\s+to\s+(a\s+)?(rep|human|agent|person)\b/,
    /\bget\s+(a\s+)?(human|rep|agent)\b/,
    /\btransfer\s+to\s+(a\s+)?(human|rep|agent)\b/,
  ];
  return patterns.some((re) => re.test(t));
}

function formatMessageTime(createdAt) {
  if (!createdAt) return '';
  const d = new Date(createdAt);
  const now = new Date();
  const sameDay = d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  return sameDay ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** Play a short tone with Web Audio API (no external files). */
function playTone(options = {}) {
  const { frequency = 440, duration = 0.1, type = 'sine', volume = 0.15 } = options;
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.frequency.value = frequency;
    oscillator.type = type;
    gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + duration);
  } catch (_) {
    /* ignore if audio not allowed */
  }
}

const sounds = {
  open: () => playTone({ frequency: 523, duration: 0.12, type: 'sine', volume: 0.12 }),
  send: () => playTone({ frequency: 660, duration: 0.08, type: 'sine', volume: 0.1 }),
  receive: () => playTone({ frequency: 392, duration: 0.1, type: 'sine', volume: 0.1 }),
  close: () => playTone({ frequency: 440, duration: 0.06, type: 'sine', volume: 0.08 }),
};

function getRelevantEndpoints(prompt) {
  const q = prompt.toLowerCase();
  const needs = new Set();

  const about = /\b(about|bio|biography|who|introduce|yourself|person|owner|overview)\b/;
  const skills = /\b(skill|technology|tech|expertise|language|framework|tool|stack|proficient)\b/;
  const services = /\b(service|offer|what do you do|hire|work with|build for me)\b/;
  const projects = /\b(project|portfolio|work|built|created|app|website|developed)\b/;
  const education = /\b(education|degree|school|university|study|qualification|certification)\b/;
  const broad = /\b(site|website|everything|tell me about|what's on|what can you|general|overview)\b/;

  if (broad.test(q)) return ['about', 'skills', 'services', 'projects', 'projectCategories', 'education'];
  if (about.test(q)) needs.add('about');
  if (skills.test(q)) needs.add('skills');
  if (services.test(q)) needs.add('services');
  if (projects.test(q)) {
    needs.add('projects');
    needs.add('projectCategories');
  }
  if (education.test(q)) needs.add('education');

  if (needs.size === 0) needs.add('about');
  return Array.from(needs);
}

async function fetchRelevantContext(endpointNames) {
  const fetches = [];
  if (endpointNames.includes('about')) fetches.push(fetchWithLog(ENDPOINTS.about).then((r) => r.json()));
  if (endpointNames.includes('skills')) fetches.push(fetchWithLog(ENDPOINTS.skills).then((r) => r.json()));
  if (endpointNames.includes('services')) fetches.push(fetchWithLog(ENDPOINTS.services).then((r) => r.json()));
  if (endpointNames.includes('projects')) fetches.push(fetchWithLog(ENDPOINTS.projects).then((r) => r.json()));
  if (endpointNames.includes('projectCategories')) fetches.push(fetchWithLog(ENDPOINTS.projectCategories).then((r) => r.json()));
  if (endpointNames.includes('education')) fetches.push(fetchWithLog(ENDPOINTS.education).then((r) => r.json()));

  const results = await Promise.all(fetches);
  let idx = 0;
  const context = {};

  if (endpointNames.includes('about')) {
    const d = results[idx++];
    context.biography = d?.[0]?.biography ?? '';
  }
  if (endpointNames.includes('skills')) {
    const skillsData = results[idx++];
    const skillsTree = {};
    (skillsData || []).forEach((s) => {
      if (s.vid === 'skills') skillsTree[s.tid] = { title: s.title, subcats: {} };
      else if (s.vid === 'skills_subcategory' && s.category) {
        if (!skillsTree[s.category]) skillsTree[s.category] = { title: '', subcats: {} };
        skillsTree[s.category].subcats[s.tid] = { title: s.title, items: [] };
      } else if (s.vid === 'skills_items' && s.category && s.subcategory) {
        const cat = skillsTree[s.category];
        if (cat?.subcats[s.subcategory]) cat.subcats[s.subcategory].items.push(s.title);
      }
    });
    const lines = [];
    Object.values(skillsTree).forEach((cat) => {
      Object.values(cat.subcats || {}).forEach((sub) => {
        if (sub.items?.length) lines.push(`- ${cat.title} > ${sub.title}: ${sub.items.join(', ')}`);
      });
    });
    context.skillsText = lines.join('\n');
  }
  if (endpointNames.includes('services')) {
    const d = results[idx++];
    context.servicesText = (d || []).map((s) => `- ${s.labelText}: ${s.descriptionText}`).join('\n');
  }
  if (endpointNames.includes('projects')) {
    const d = results[idx++];
    context.projectsText = (d || [])
      .map((p) => {
        const parts = [`- ${p.title} (${p.category})`];
        if (p.summary) parts.push(`: ${p.summary}`);
        if (p.technologies) parts.push(` [${p.technologies}]`);
        return parts.join('');
      })
      .join('\n');
  }
  if (endpointNames.includes('projectCategories')) {
    const d = results[idx++];
    context.categoriesText = (d || []).filter((c) => c.is_visible).map((c) => c.title).join(', ');
  }
  if (endpointNames.includes('education')) {
    const d = results[idx++];
    const ed = d?.[0] ?? {};
    context.educationText = [ed.education, ed.education2].filter(Boolean).map(stripHtml).join(' ');
  }

  return context;
}

const DEFAULT_PROMPT = `You are a helpful assistant for this portfolio website, which exists to sell the owner's services. Your goal is to help visitors understand what's offered and encourage them to get in touch or hire. Answer questions using ONLY the following context from the site. Be friendly, concise, and professional. When relevant, naturally suggest contacting or hiring. If the answer is not in the context, say you don't have that information and suggest exploring the relevant section or the Contact / Let's Talk page.`;

function buildSystemPrompt(context, basePrompt = DEFAULT_PROMPT) {
  const parts = [basePrompt];

  if (context.biography) parts.push(`\n\n## About / Biography\n${context.biography}`);
  if (context.educationText) parts.push(`\n\n## Education\n${context.educationText}`);
  if (context.skillsText) parts.push(`\n\n## Skills\n${context.skillsText}`);
  if (context.servicesText) parts.push(`\n\n## Services Offered\n${context.servicesText}`);
  if (context.projectsText) parts.push(`\n\n## Projects\n${context.projectsText}`);
  if (context.categoriesText) parts.push(`\n\n## Portfolio Categories\n${context.categoriesText}`);

  parts.push(`\n\n## Contact\nVisitors can use the Contact section or "Let's Talk" to get in touch.`);
  parts.push(`\n\n## Page Links\n${PAGE_ROUTES}`);
  return parts.join('');
}

const POLL_INTERVAL_MS = 3000;

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastContext, setLastContext] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  const [chatStatus, setChatStatus] = useState('bot'); // 'bot' | 'awaiting_agent' | 'live_agent' | 'closed'
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [visitorName, setVisitorName] = useState('');
  const [visitorEmail, setVisitorEmail] = useState('');
  const [collectingContact, setCollectingContact] = useState(false);
  const [contactError, setContactError] = useState('');
  const sessionIdRef = useRef(null);
  const conversationIdRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  if (!sessionIdRef.current) sessionIdRef.current = getSessionId();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const saveConversation = async (messagesToAppend, requestAgent = false) => {
    const url = ENDPOINTS.chatSave;
    const body = {
      session_id: sessionIdRef.current,
      messages: messagesToAppend,
      request_agent: requestAgent,
    };
    if (conversationIdRef.current) body.conversation_id = conversationIdRef.current;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (data.conversation_id) conversationIdRef.current = data.conversation_id;
    return data;
  };

  const closeConversation = async () => {
    if (!conversationIdRef.current) return;
    const url = ENDPOINTS.chatSave;
    const body = {
      session_id: sessionIdRef.current,
      conversation_id: conversationIdRef.current,
      messages: [],
      close_conversation: true,
    };
    try {
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch (_) {
      // swallow errors on best-effort close
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-terminate chat after 5 minutes of inactivity once a conversation exists
  useEffect(() => {
    if (!conversationIdRef.current || chatStatus === 'closed') return;
    const TIMEOUT_MS = 5 * 60 * 1000;
    const id = setInterval(() => {
      if (Date.now() - lastActivity >= TIMEOUT_MS) {
        setChatStatus('closed');
        setInput('');
        closeConversation();
      }
    }, 30000);
    return () => clearInterval(id);
  }, [lastActivity, chatStatus]);

  // When conversation is closed (expired or from server), clear input so user cannot send
  useEffect(() => {
    if (chatStatus === 'closed') setInput('');
  }, [chatStatus]);

  // Poll for conversation when waiting for or in live agent mode
  useEffect(() => {
    if (chatStatus !== 'awaiting_agent' && chatStatus !== 'live_agent') return;
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;
    const url = typeof ENDPOINTS.chatConversation === 'function' ? ENDPOINTS.chatConversation(sessionId) : ENDPOINTS.chatConversation + '?session_id=' + encodeURIComponent(sessionId);
    const tick = async () => {
      try {
        const res = await fetch(url);
        const data = await res.json();
        if (data.messages) {
          const newMessages = data.messages.map((m) => ({ role: m.role, content: m.content, created_at: m.created_at }));
          setMessages((prev) => {
            if (prev.length === newMessages.length && prev.length > 0) {
              const prevLast = prev[prev.length - 1];
              const nextLast = newMessages[newMessages.length - 1];
              if (
                prevLast.role === nextLast.role &&
                prevLast.content === nextLast.content &&
                prevLast.created_at === nextLast.created_at
              ) {
                return prev;
              }
            }
            setLastActivity(Date.now());
            return newMessages;
          });
        }
        if (data.status) setChatStatus(data.status);
      } catch (_) {}
    };
    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [chatStatus]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const requestLiveAgent = async () => {
    if (isLoading) return;
    if (!visitorName.trim() || !visitorEmail.trim()) {
      setCollectingContact(true);
      setContactError('');
      return;
    }
    setIsLoading(true);
    try {
      const contactMessage = {
        role: 'user',
        content: `Contact info: Name: ${visitorName.trim()}, Email: ${visitorEmail.trim()}`,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, contactMessage]);
      const data = await saveConversation([contactMessage], true);
      setChatStatus(data.status || 'awaiting_agent');
    } catch (_) {
      setChatStatus('awaiting_agent');
    }
    setLastActivity(Date.now());
    setIsLoading(false);
  };

  const sendMessage = async () => {
    if (chatStatus === 'closed') return;
    const text = input.trim();
    if (!text || isLoading) return;

    // If we're in the middle of collecting contact info for a human handoff,
    // use this message to finalize the handoff (no bot response).
    if (chatStatus === 'bot' && collectingContact) {
      if (!visitorName.trim() || !visitorEmail.trim()) {
        setContactError('Please enter both your name and email to continue.');
        return;
      }
      const userMessageForAgent = { role: 'user', content: text, created_at: new Date().toISOString() };
      const contactMessage = {
        role: 'user',
        content: `Contact info: Name: ${visitorName.trim()}, Email: ${visitorEmail.trim()}`,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMessageForAgent, contactMessage]);
      setLastActivity(Date.now());
      setInput('');
      setIsLoading(true);
      sounds.send();
      try {
        await saveConversation([userMessageForAgent, contactMessage], true);
        setChatStatus('awaiting_agent');
      } catch (_) {
        setChatStatus('awaiting_agent');
      } finally {
        setIsLoading(false);
        setCollectingContact(false);
        setContactError('');
      }
      sounds.receive();
      return;
    }

    const userMessage = { role: 'user', content: text, created_at: new Date().toISOString() };
    // Message with timestamp for local UI + storage
    setMessages((prev) => [...prev, userMessage]);
    setLastActivity(Date.now());
    setInput('');
    setIsLoading(true);
    sounds.send();

    if (chatStatus === 'live_agent') {
      try {
        await saveConversation([userMessage]);
      } catch (_) {}
      setIsLoading(false);
      return;
    }

    if (chatStatus === 'bot' && wantsToSpeakToHuman(text)) {
      if (!visitorName.trim() || !visitorEmail.trim()) {
        setCollectingContact(true);
        setContactError('');
        setIsLoading(false);
        return;
      }
      try {
        const contactMessage = {
          role: 'user',
          content: `Contact info: Name: ${visitorName.trim()}, Email: ${visitorEmail.trim()}`,
          created_at: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, contactMessage]);
        await saveConversation([userMessage, contactMessage], true);
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: "Connecting you with a team member. You can keep typing below—someone will join shortly.", created_at: new Date().toISOString() },
        ]);
        setChatStatus('awaiting_agent');
      } catch (_) {
        setChatStatus('awaiting_agent');
      }
      sounds.receive();
      setIsLoading(false);
      return;
    }

    const lastUserMsg = messages.filter((m) => m.role === 'user').pop()?.content ?? '';
    const promptForRelevance = text.length < 20 && lastUserMsg ? `${lastUserMsg} ${text}` : text;
    const endpoints = getRelevantEndpoints(promptForRelevance);

    let basePrompt = DEFAULT_PROMPT;
    let context = {};
    try {
      const [promptRes, ctx] = await Promise.all([
        fetch(ENDPOINTS.chatbotPrompt).then((r) => r.json()),
        fetchRelevantContext(endpoints),
      ]);
      basePrompt = (promptRes?.prompt || '').trim() || DEFAULT_PROMPT;
      context = ctx;
    } catch {
      try {
        const promptRes = await fetch(ENDPOINTS.chatbotPrompt).then((r) => r.json());
        basePrompt = (promptRes?.prompt || '').trim() || DEFAULT_PROMPT;
      } catch {
        /* use DEFAULT_PROMPT */
      }
    }
    setLastContext({ ...context, _basePrompt: basePrompt });

    const systemPrompt = Object.keys(context).length
      ? buildSystemPrompt(context, basePrompt)
      : basePrompt;

    try {
      // Messages sent to Groq must NOT include extra fields like created_at
      const chatMessages = [
        { role: 'system', content: systemPrompt },
        ...messages.map((m) => ({ role: m.role, content: m.content })),
        { role: 'user', content: text },
      ];

      const res = await fetchWithLog(ENDPOINTS.chat, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: chatMessages,
          model: 'llama-3.3-70b-versatile',
          temperature: 0.7,
          max_tokens: 1024,
        }),
      });

      let data;
      try {
        data = await res.json();
      } catch (parseErr) {
        console.error('[chat] Failed to parse JSON from /chat.php', parseErr);
        throw new Error(`Invalid JSON response (${res.status})`);
      }

      if (!res.ok) {
        console.error('[chat] /chat.php returned error status', {
          status: res.status,
          data,
        });
        throw new Error(data.error || `Request failed (${res.status})`);
      }

      const content = data.choices?.[0]?.message?.content ?? 'Sorry, I could not generate a response.';
      const assistantMessage = { role: 'assistant', content, created_at: new Date().toISOString() };
      setMessages((prev) => [...prev, assistantMessage]);
      sounds.receive();
      try {
        await saveConversation([userMessage, assistantMessage]);
      } catch (_) {}
    } catch (err) {
      console.error('[chat] Request to /chat.php failed', err);
      const assistantMessage = {
        role: 'assistant',
        content: `Sorry, something went wrong: ${err.message}. Please ensure the chat API is configured with a valid Groq API key.`,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
      sounds.receive();
      try {
        await saveConversation([userMessage, assistantMessage]);
      } catch (_) {}
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (chatStatus === 'closed') return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="chatbot-container">
      {isOpen && (
        <>
          <div className="chatbot-overlay" onClick={() => { sounds.close(); setIsOpen(false); }} aria-hidden="true" />
          <div className="chatbot-window">
          <div className="chatbot-header">
            <span className="chatbot-title">Chat</span>
            <div className="chatbot-header-actions">
              {process.env.NODE_ENV === 'development' && (
                <button
                  type="button"
                  className={`chatbot-debug-toggle ${showDebug ? 'active' : ''}`}
                  onClick={() => setShowDebug(!showDebug)}
                  title="Toggle debug view"
                  aria-label="Toggle debug view"
                >
                  Debug
                </button>
              )}
              <button
                type="button"
                className="chatbot-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close chat"
              >
                ×
              </button>
            </div>
          </div>
          {process.env.NODE_ENV === 'development' && showDebug && (
            <div className="chatbot-debug-panel">
              <pre className="chatbot-debug-content">
                {lastContext
                  ? buildSystemPrompt(
                      Object.fromEntries(Object.entries(lastContext).filter(([k]) => k !== '_basePrompt')),
                      lastContext._basePrompt || DEFAULT_PROMPT
                    )
                  : 'Context will appear here after you send a message.'}
              </pre>
            </div>
          )}
          <div className="chatbot-messages">
            {messages.length === 0 && (
              <div className="chatbot-welcome">
                Hi! 👋 I'm here to help you discover how we can work together. Ask me about my services, projects, or let's discuss your next idea—I'd love to hear from you!
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`chatbot-msg chatbot-msg-${msg.role}`}>
                {msg.role === 'agent' && (
                  <div className="chatbot-msg-agent-label" aria-hidden="true">
                    <span className="chatbot-msg-agent-icon" aria-hidden="true">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
                    </span>
                    Live Agent
                  </div>
                )}
                <div className="chatbot-msg-content">
                  {(msg.role === 'assistant' || msg.role === 'agent') ? renderMessageWithLinks(msg.content, () => setIsOpen(false)) : msg.content}
                </div>
                {msg.created_at && <span className="chatbot-msg-time" aria-label="Sent">{formatMessageTime(msg.created_at)}</span>}
              </div>
            ))}
            {isLoading && (
              <div className="chatbot-msg chatbot-msg-assistant chatbot-typing">
                <span className="typing-dot" />
                <span className="typing-dot" />
                <span className="typing-dot" />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          {(chatStatus === 'awaiting_agent' || chatStatus === 'live_agent' || chatStatus === 'closed') && (
            <div className="chatbot-agent-banner" role="status">
              {chatStatus === 'awaiting_agent' && 'A team member will join shortly. You can keep typing below.'}
              {chatStatus === 'live_agent' && 'You are chatting with a live agent.'}
              {chatStatus === 'closed' && 'This chat has ended due to inactivity. You can start a new chat anytime.'}
            </div>
          )}
          <div className="chatbot-input-area">
            {chatStatus === 'bot' && (
              <button
                type="button"
                className="chatbot-transfer-agent"
                onClick={requestLiveAgent}
                disabled={isLoading}
              >
                Talk to a human
              </button>
            )}
            {collectingContact && chatStatus !== 'closed' && (
              <div className="chatbot-contact-form">
                <div className="chatbot-contact-heading">
                  Before I connect you to a live agent, what's your name and email?
                </div>
                <div className="chatbot-contact-fields">
                  <input
                    type="text"
                    placeholder="Your name"
                    value={visitorName}
                    onChange={(e) => setVisitorName(e.target.value)}
                  />
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={visitorEmail}
                    onChange={(e) => setVisitorEmail(e.target.value)}
                  />
                </div>
                {contactError && <div className="chatbot-contact-error">{contactError}</div>}
              </div>
            )}
            {chatStatus === 'closed' && (
              <button
                type="button"
                className="chatbot-transfer-agent"
                onClick={() => {
                  sessionIdRef.current = getSessionId();
                  conversationIdRef.current = null;
                  setMessages([]);
                  setChatStatus('bot');
                  setInput('');
                  setLastContext(null);
                  setLastActivity(Date.now());
                }}
              >
                Start a new chat
              </button>
            )}
            <div className="chatbot-input-row">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  chatStatus === 'live_agent'
                    ? 'Message the agent...'
                    : chatStatus === 'closed'
                      ? 'Chat ended. Start a new chat to continue.'
                      : 'Type your message...'
                }
                rows={1}
                disabled={isLoading || chatStatus === 'closed'}
              />
              <button
                type="button"
                className="chatbot-send"
                onClick={sendMessage}
                disabled={!input.trim() || isLoading || chatStatus === 'closed'}
                aria-label="Send message"
              >
                Send
              </button>
            </div>
          </div>
        </div>
        </>
      )}
      <button
        type="button"
        className={`chatbot-toggle ${isOpen ? 'chatbot-toggle-open' : ''}`}
        onClick={() => {
          const next = !isOpen;
          if (next) sounds.open();
          else sounds.close();
          setIsOpen(next);
        }}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? '×' : '💬'}
      </button>
    </div>
  );
}
